/**
 * ZON-275 — Notifications: school news for the schools on My Schools, plus
 * new fits (ZON-249). Newest first in Today / This week / Earlier sections.
 * No counts, no read state. Also the landing page for the digest email's
 * "See all" and the alert email's "Draft a note" (?draft=<updateId>).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import TwoPaneLayout from "@/components/TwoPaneLayout";
import Sidebar from "@/components/Sidebar";
import { AlertCard, FitsCard, NewsRowCard, ResultsCard } from "@/components/news/NewsCards";
import {
  buildSections,
  draftPrompt,
  previewFromFit,
  previewFromNews,
  type FeedEntry,
  type PreviewSchool,
} from "@/components/news/newsFeed";
import { markSeen } from "@/components/news/lastSeen";
import { SchoolPreviewPanel } from "@/components/school/SchoolPreviewPanel";
import { useEndUser } from "@/contexts/EndUserContext";
import { trackNotificationsViewed, trackSchoolNewsAction } from "@/analytics/analytics";
import { sourceProps } from "@/analytics/source";
import {
  addToPipeline,
  fetchPipeline,
  PIPELINE_UPDATED_EVENT,
  type PipelineEntry,
} from "@/services/pipeline";
import {
  fetchNotifications,
  type FitSchool,
  type NewsFilter,
  type NewsItem,
  type NotificationItem,
} from "@/services/schoolNews";
import { PAGE_SUBTITLE_CLASS, PAGE_TITLE_CLASS } from "@/constants/design";

const FILTERS: { id: NewsFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "news", label: "School Watch" },
  { id: "fits", label: "New fits" },
];

type Status = "loading" | "ready" | "error" | "disabled";

function entryKey(entry: FeedEntry): string {
  return entry.kind === "results" ? entry.key : entry.item.id;
}

type SchoolNewsActionName = Parameters<typeof trackSchoolNewsAction>[0]["action"];

export function NotificationsContent({ now }: { now?: Date }) {
  const navigate = useNavigate();
  const userId = useEndUser().authState?.userId ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<NewsFilter>("all");
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [loadingMore, setLoadingMore] = useState(false);
  const [addingTeamIds, setAddingTeamIds] = useState<Set<string>>(new Set());
  // The school whose panel is open, if any (a new fit or a school in the news).
  const [previewSchool, setPreviewSchool] = useState<PreviewSchool | null>(null);
  // My Schools, loaded once on first need, for a saved school's fit and details.
  const pipelineByTeam = useRef<Promise<Map<string, PipelineEntry>> | null>(null);
  const adding = useRef(new Set<string>());
  const draftHandled = useRef(false);
  // Bumped on every (re)load so a slow response for an earlier filter is dropped.
  const requestSeq = useRef(0);
  const draftId = searchParams.get("draft");

  // Every action here is on the Notifications page; `source` says whether the
  // visit came from a School Watch email (?src=).
  const trackAction = useCallback(
    (props: { action: SchoolNewsActionName; type?: string; teamId?: string }) =>
      trackSchoolNewsAction({ ...props, surface: "notifications", ...sourceProps(searchParams) }),
    [searchParams]
  );
  const trackRead = useCallback(
    (item: NewsItem) => trackAction({ action: "read", type: item.type, teamId: item.teamId }),
    [trackAction]
  );

  const load = useCallback(async (which: NewsFilter) => {
    const seq = ++requestSeq.current;
    setStatus("loading");
    setLoadingMore(false);
    try {
      const page = await fetchNotifications({ filter: which });
      if (seq !== requestSeq.current) return;
      if (!page.enabled) {
        setStatus("disabled");
        return;
      }
      setItems(page.items);
      setNextCursor(page.nextCursor);
      setStatus("ready");
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("Failed to load notifications:", error);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    trackNotificationsViewed();
  }, []);

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  // Seeing the feed is what clears the nav dot (remembered in this browser).
  useEffect(() => {
    if (status === "ready") markSeen(userId);
  }, [status, userId]);

  const openDraft = useCallback(
    (item: NewsItem) => {
      trackAction({ action: "draft", type: item.type, teamId: item.teamId });
      navigate("/chat", { state: draftPrompt(item) });
    },
    [navigate, trackAction]
  );

  // ?draft=<updateId> from an alert email: go straight to the draft, once,
  // and only if that item is in the page we loaded.
  useEffect(() => {
    if (!draftId || draftHandled.current || status !== "ready") return;
    draftHandled.current = true;
    const item = items.find(
      (candidate): candidate is NewsItem => candidate.feed === "news" && candidate.id === draftId
    );
    // Drop ?draft from this history entry first, so Back from Ask Coach lands
    // on plain Notifications and does not bounce forward and re-send the prompt.
    const next = new URLSearchParams(searchParams);
    next.delete("draft");
    setSearchParams(next, { replace: true });
    if (item) openDraft(item);
  }, [draftId, items, openDraft, searchParams, setSearchParams, status]);

  function loadPipelineByTeam(): Promise<Map<string, PipelineEntry>> {
    if (!pipelineByTeam.current) {
      pipelineByTeam.current = fetchPipeline()
        .then(
          response =>
            new Map(
              response.pipeline
                .filter(entry => entry.school.teamId)
                .map(entry => [entry.school.teamId as string, entry])
            )
        )
        .catch(error => {
          console.error("Failed to load My Schools for the school panel:", error);
          return new Map<string, PipelineEntry>();
        });
    }
    return pipelineByTeam.current;
  }

  /** Open the panel for a school in the news, then fill in its fit from My Schools. */
  function openNewsSchool(item: NewsItem) {
    setPreviewSchool(previewFromNews(item));
    void loadPipelineByTeam().then(entries => {
      const entry = entries.get(item.teamId);
      if (!entry) return;
      setPreviewSchool(current =>
        current && current.saved && current.teamId === item.teamId
          ? {
              ...current,
              fit: entry.golfFit ?? null,
              division: entry.school.division,
              city: entry.school.city,
              state: entry.school.state,
              gender: entry.school.gender,
              logoUrl: current.logoUrl || entry.school.logoUrl,
            }
          : current
      );
    });
  }

  /** Add from the panel: the panel knows the school, the feed holds the fit record. */
  function addPreviewed(school: PreviewSchool) {
    for (const item of items) {
      if (item.feed !== "fits") continue;
      const match = item.schools.find(candidate => candidate.teamId === school.teamId);
      if (match) {
        void addFit(match);
        return;
      }
    }
  }

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    const seq = requestSeq.current;
    setLoadingMore(true);
    trackAction({ action: "load_more" });
    try {
      const page = await fetchNotifications({ filter, before: nextCursor });
      if (seq !== requestSeq.current) return;
      setItems(current => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("Failed to load more notifications:", error);
      toast.error("More notifications could not be loaded");
    } finally {
      if (seq === requestSeq.current) setLoadingMore(false);
    }
  }

  function removeFitSchool(teamId: string) {
    setPreviewSchool(current => (current?.teamId === teamId ? null : current));
    setItems(current =>
      current
        .map(item =>
          item.feed === "fits"
            ? { ...item, schools: item.schools.filter(s => s.teamId !== teamId) }
            : item
        )
        .filter(item => item.feed !== "fits" || item.schools.length > 0)
    );
  }

  async function addFit(school: FitSchool) {
    if (adding.current.has(school.teamId)) return;
    adding.current.add(school.teamId);
    setAddingTeamIds(new Set(adding.current));
    const name = school.schoolName || "School";
    try {
      await addToPipeline({
        schoolName: name,
        teamId: school.teamId,
        division: school.division,
        conference: school.conference,
        state: school.state,
        gender: school.gender,
        headCoach: school.headCoach,
        headCoachEmail: school.headCoachEmail,
      });
      trackAction({ action: "add_fit", teamId: school.teamId });
      removeFitSchool(school.teamId);
      toast.success(`${name} added to My Schools`);
      window.dispatchEvent(new Event(PIPELINE_UPDATED_EVENT));
    } catch (error) {
      // The API answers 409 when the school is already on the list: that is
      // the outcome the user wanted, so take it off the card.
      if (/already in pipeline|\b409\b/i.test(error instanceof Error ? error.message : "")) {
        removeFitSchool(school.teamId);
        toast.success(`${name} is already on My Schools`);
        return;
      }
      console.error("Failed to add school:", error);
      toast.error(`${name} could not be added`);
    } finally {
      adding.current.delete(school.teamId);
      setAddingTeamIds(new Set(adding.current));
    }
  }

  const sections = useMemo(() => buildSections(items, now ?? new Date()), [items, now]);

  if (status === "disabled") return <Navigate to="/pipeline" replace />;

  return (
    <div className="relative flex-1 overflow-y-auto bg-surface">
      <div className="mx-auto w-full max-w-2xl px-4 py-6">
        <h1 className={PAGE_TITLE_CLASS}>Notifications</h1>
        <p className={PAGE_SUBTITLE_CLASS}>
          School Watch news from the schools on your list, and new schools that fit your game.
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {FILTERS.map(option => (
            <button
              key={option.id}
              type="button"
              aria-pressed={filter === option.id}
              onClick={() => {
                if (filter === option.id) return;
                trackAction({ action: "filter", type: option.id });
                setFilter(option.id);
              }}
              className={
                "rounded-full border px-3 py-1 text-[11px] font-bold transition-colors " +
                (filter === option.id
                  ? "border-forest bg-forest text-white"
                  : "border-border bg-white text-forest-soft hover:bg-muted")
              }
            >
              {option.label}
            </button>
          ))}
        </div>

        {status === "loading" && <p className="mt-8 text-[11px] text-subtle">Loading…</p>}

        {status === "error" && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-4 text-[12px] text-forest">
            <p>Notifications could not be loaded</p>
            <button
              type="button"
              onClick={() => void load(filter)}
              className="mt-2 text-[11px] font-bold text-primary-text hover:underline"
            >
              Try again
            </button>
          </div>
        )}

        {status === "ready" && sections.length === 0 && (
          <div className="mt-8 rounded-2xl border border-border bg-white p-5 text-center">
            <p className="font-display text-base font-extrabold tracking-[-0.025em] text-forest">
              Nothing new at your schools yet
            </p>
            <p className="mt-1 text-[11px] text-subtle">
              School Watch posts tournament results, schedules and coaching news here, along with
              new schools that fit your game.
            </p>
          </div>
        )}

        {status === "ready" &&
          sections.map(section => (
            <section key={section.id} aria-label={section.label} className="mt-6">
              <h2 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.12em] text-subtle">
                {section.label}
              </h2>
              <div className="flex flex-col gap-2">
                {section.entries.map(entry => {
                  const key = entryKey(entry);
                  if (entry.kind === "alert") {
                    return (
                      <AlertCard
                        key={key}
                        item={entry.item}
                        onDraft={openDraft}
                        onOpen={openNewsSchool}
                        onRead={trackRead}
                      />
                    );
                  }
                  if (entry.kind === "results")
                    return (
                      <ResultsCard
                        key={key}
                        items={entry.items}
                        onOpen={openNewsSchool}
                        onRead={trackRead}
                      />
                    );
                  if (entry.kind === "fits") {
                    return (
                      <FitsCard
                        key={key}
                        item={entry.item}
                        onAdd={school => void addFit(school)}
                        onOpen={school => setPreviewSchool(previewFromFit(school))}
                        addingTeamIds={addingTeamIds}
                      />
                    );
                  }
                  return (
                    <NewsRowCard
                      key={key}
                      item={entry.item}
                      onOpen={openNewsSchool}
                      onRead={trackRead}
                    />
                  );
                })}
              </div>
            </section>
          ))}

        {status === "ready" && nextCursor && (
          <div className="mt-6 text-center">
            <button
              type="button"
              disabled={loadingMore}
              onClick={() => void loadMore()}
              className="rounded-lg border border-border bg-white px-4 py-2 text-[11px] font-bold text-forest hover:bg-muted disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
      <SchoolPreviewPanel
        school={previewSchool}
        onClose={() => setPreviewSchool(null)}
        onAdd={addPreviewed}
        adding={previewSchool ? addingTeamIds.has(previewSchool.teamId) : false}
      />
    </div>
  );
}

const Notifications = () => {
  const navigate = useNavigate();
  const { isSidebarOpen, closeSidebar, chatHistory, playerSummary } = useEndUser();

  return (
    <TwoPaneLayout
      isSidebarOpen={isSidebarOpen}
      onCloseSidebar={closeSidebar}
      sidebar={
        <Sidebar
          chatHistory={chatHistory}
          onNewChat={() => navigate("/chat")}
          onClose={closeSidebar}
          onSelectChat={id => navigate(`/chat?thread=${id}`)}
          isLinked={playerSummary.isLinked}
        />
      }
      main={<NotificationsContent />}
    />
  );
};

export default Notifications;
